import { Injectable } from '@angular/core';
import { VariableService } from '@services/variable/variable.service';
import { Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TinymceConfigService {

  constructor(private variableService: VariableService) {}

  getConfig(): any {
    return {
      height: '100%',
      menubar: false,
      statusbar: false,
      plugins: [
        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
        'insertdatetime', 'media', 'table', 'preview', 'help', 'wordcount'
      ],
      toolbar: [
        'undo redo | blocks | bold italic underline strikethrough | ',
        'alignleft aligncenter alignright alignjustify | ',
        'bullist numlist outdent indent | removeformat | ',
        'insertvariable procesarvariables | fullscreen help'
      ].join(''),
      content_style: `
        body {
          font-family: -apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, Helvetica Neue, sans-serif;
          font-size: 14px;
          line-height: 1.6;
          color: #333;
          margin: 16px;
        }
        .variable-highlight {
          background-color: #e3f2fd;
          color: #1976d2;
          padding: 2px 4px;
          border-radius: 3px;
          font-family: 'Courier New', monospace;
          font-weight: 500;
          border: 1px solid #bbdefb;
        }
        .variable-highlight:hover {
          background-color: #bbdefb;
        }
      `,
      setup: (editor: any) => {
        this.setupVariableHighlight(editor);
        this.setupCustomButtons(editor);
        this.setupAutoComplete(editor);
      }
    };
  }

  private setupVariableHighlight(editor: any): void {
    editor.on('input keyup', () => {
      this.highlightVariablesInEditor(editor);
    });

    editor.on('SetContent', () => {
      this.highlightVariablesInEditor(editor);
    });
  }

  private setupCustomButtons(editor: any): void {
    // Botón Insertar Variable
    editor.ui.registry.addButton('insertvariable', {
      text: 'Variable',
      icon: 'code-sample',
      tooltip: 'Insertar Variable',
      onAction: () => {
        this.openVariableSelector(editor);
      }
    });

    // Botón Procesar Variables
    editor.ui.registry.addButton('procesarvariables', {
      text: 'Procesar',
      icon: 'refresh',
      tooltip: 'Procesar Variables',
      onAction: () => {
        this.processVariables(editor);
      }
    });
  }

  private setupAutoComplete(editor: any): void {
    editor.on('keydown', (e: KeyboardEvent) => {
      if (e.key === '{') {
        const selection = editor.selection;
        const range = selection.getRng();
        const textNode = range.startContainer;
        const offset = range.startOffset;

        if (textNode.textContent?.charAt(offset - 1) === '{') {
          // Usuario escribió {{, mostrar autocompletado
          setTimeout(() => {
            this.showVariableAutocomplete(editor);
          }, 100);
        }
      }
    });
  }

  private highlightVariablesInEditor(editor: any): void {
    const content = editor.getContent();
    const variableRegex = /\{\{([^}]+)\}\}/g;
    let match;
    const ranges: any[] = [];

    // Encontrar todas las variables en el contenido
    while ((match = variableRegex.exec(content)) !== null) {
      const startIndex = match.index;
      const endIndex = startIndex + match[0].length;
      ranges.push({ start: startIndex, end: endIndex, text: match[0] });
    }

    // Aplicar estilo a las variables encontradas
    if (ranges.length > 0) {
      const highlightedContent = this.highlightVariables(content);
      if (highlightedContent !== content) {
        const bookmark = editor.selection.getBookmark();
        editor.setContent(highlightedContent);
        editor.selection.moveToBookmark(bookmark);
      }
    }
  }

  private highlightVariables(content: string): string {
    const variableRegex = /\{\{([^}]+)\}\}/g;
    return content.replace(variableRegex,
      '<span class="variable-highlight">{{$1}}</span>'
    );
  }

  private openVariableSelector(editor: any): void {
    // Esto se implementará cuando tengamos el componente de variables
    console.log('Abrir selector de variables');

    // Por ahora, insertar una variable de ejemplo
    editor.insertContent('{{Variable.Ejemplo}}');
  }

  private showVariableAutocomplete(editor: any): void {
    // Obtener variables disponibles
    this.variableService.getAll().subscribe(variables => {
      const suggestions = variables.map(v => ({
        text: `{{${v.codigo}}}`,
        value: `{{${v.codigo}}}`,
        meta: v.descripcion || v.tipoVariable?.descripcion
      }));

      // Aquí implementarías un dropdown de autocompletado
      console.log('Sugerencias de variables:', suggestions);
    });
  }

  private processVariables(editor: any): void {
    const content = editor.getContent();

    // Por ahora solo mostrar las variables encontradas
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const variables: string[] = [];
    let match;

    while ((match = variableRegex.exec(content)) !== null) {
      variables.push(match[1]);
    }

    if (variables.length > 0) {
      alert(`Variables encontradas: ${variables.join(', ')}\n\nEsta funcionalidad se implementará cuando tengas la API de procesamiento.`);
    } else {
      alert('No se encontraron variables para procesar.');
    }
  }
}
